import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Icon, Text } from '../../../../components';
import { ART_STYLE } from '../../../../theme/tokens';
import { MASCOT_AUTO, MASCOT_NONE, type ArtStyleMascot, type MascotPick } from '../../art-style-api';
import { useActiveArtStyle } from '../../useActiveArtStyle';
import { usePortalMenu, type PortalMenuAlign } from '../../usePortalMenu';
import { MascotAvatar } from '../MascotAvatar';
import {
  Check,
  MenuPanel,
  Option,
  OptionCopy,
  OptionHint,
  OptionLabel,
  Pill,
  PillLabel,
  PillName,
  Stack,
} from './MascotPicker.styles';

export type MascotPickerProps = {
  /** The pick a generation sends as `mascot_id`: `MASCOT_AUTO` (the default), `MASCOT_NONE` or a mascot's id. */
  value: MascotPick;
  onChange: (value: MascotPick) => void;
  /** A run is in flight (or the setup is locked): the pill stays, greyed. */
  disabled?: boolean;
  /** Which edge of the pill the list lines up with (`start` by default). */
  align?: PortalMenuAlign;
  /** The cast to list instead of the ACTIVE style's: a picked style's mascots (a draw in another style). */
  cast?: ArtStyleMascot[];
  className?: string;
};

/** How many avatars "Auto" stacks on the pill. */
const AUTO_STACK = 3;

const firstImage = (mascot: ArtStyleMascot): string | undefined => mascot.images[0]?.url;

/**
 * Which of the brand's own characters a generation draws, beside the "Art style" chip on a generate sheet: Auto
 * (the default: the service rotates across the cast), No mascot, or one named mascot. It lists the ACTIVE
 * style's cast (only mascots with at least one image: one without has nothing to draw or show) and is hidden
 * while there is no active style or no such mascot. Given a `cast` (a picked style's), it lists that one instead.
 * Controlled: the sheet keeps the pick and sends it through `enqueueGeneration` (`mascotId`). A pick the cast no
 * longer has shows as Auto (the service treats it so too).
 */
export const MascotPicker = ({
  value,
  onChange,
  disabled = false,
  align = 'start',
  cast: castOf,
  className,
}: MascotPickerProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const active = useActiveArtStyle();
  const menu = usePortalMenu<HTMLButtonElement>({ align });
  const cast = (castOf ?? active?.mascots ?? []).filter((mascot) => mascot.images.length > 0);
  if ((!castOf && !active) || cast.length === 0) return null;

  const picked = value === MASCOT_NONE ? MASCOT_NONE : (cast.find((mascot) => mascot.id === value)?.id ?? MASCOT_AUTO);
  const pickedMascot = cast.find((mascot) => mascot.id === picked);
  const nameOf = (mascot: ArtStyleMascot): string => mascot.name.trim() || t('Unnamed character');

  const autoStack = (size: number): React.ReactElement => (
    <Stack>
      {cast.slice(0, AUTO_STACK).map((mascot) => (
        <MascotAvatar key={mascot.id} url={firstImage(mascot)} size={size} />
      ))}
    </Stack>
  );

  const choose = (next: MascotPick) => (): void => {
    menu.close(true);
    if (next !== value) onChange(next);
  };

  const pillAvatar = pickedMascot ? (
    <MascotAvatar url={firstImage(pickedMascot)} size={ART_STYLE.MASCOT_PILL_AVATAR_PX} />
  ) : picked === MASCOT_NONE ? (
    <MascotAvatar size={ART_STYLE.MASCOT_PILL_AVATAR_PX} muted />
  ) : (
    autoStack(ART_STYLE.MASCOT_PILL_AVATAR_PX)
  );

  const pillLabel =
    picked === MASCOT_NONE ? (
      <PillName text="No character" tag="span" />
    ) : (
      <>
        <Text text="Character:" tag="span" />{' '}
        {pickedMascot ? <PillName raw text={nameOf(pickedMascot)} tag="span" /> : <PillName text="Auto" tag="span" />}
      </>
    );

  return (
    <>
      <Pill
        ref={menu.triggerRef}
        type="button"
        className={className}
        $open={menu.open}
        aria-haspopup="menu"
        aria-expanded={menu.open}
        aria-label={t('Character: {{name}}', {
          name: pickedMascot ? nameOf(pickedMascot) : picked === MASCOT_NONE ? t('No character') : t('Auto'),
        })}
        title={t('Which of your characters appears. Auto picks a different one each time.')}
        disabled={disabled}
        onClick={() => {
          if (!disabled) menu.setOpen(!menu.open);
        }}
      >
        {pillAvatar}
        <PillLabel>{pillLabel}</PillLabel>
        <Icon name="chevron-down" size={12} />
      </Pill>
      {menu.open
        ? createPortal(
            <MenuPanel
              ref={menu.panelRef}
              role="menu"
              aria-label={t('Character')}
              $shown={menu.place !== null}
              style={menu.panelStyle}
              onKeyDown={menu.onItemKeyDown}
            >
              <Option
                type="button"
                role="menuitemradio"
                aria-checked={picked === MASCOT_AUTO}
                $destructive={false}
                onClick={choose(MASCOT_AUTO)}
              >
                {autoStack(ART_STYLE.MASCOT_MENU_AVATAR_PX)}
                <OptionCopy>
                  <OptionLabel text="Auto" tag="span" />
                  <OptionHint text="A different one each time" tag="span" />
                </OptionCopy>
                <Check $on={picked === MASCOT_AUTO} aria-hidden="true">
                  <Icon name="check" size={14} />
                </Check>
              </Option>
              <Option
                type="button"
                role="menuitemradio"
                aria-checked={picked === MASCOT_NONE}
                $destructive={false}
                onClick={choose(MASCOT_NONE)}
              >
                <MascotAvatar size={ART_STYLE.MASCOT_MENU_AVATAR_PX} muted />
                <OptionCopy>
                  <OptionLabel text="No character" tag="span" />
                </OptionCopy>
                <Check $on={picked === MASCOT_NONE} aria-hidden="true">
                  <Icon name="check" size={14} />
                </Check>
              </Option>
              {cast.map((mascot) => (
                <Option
                  key={mascot.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={picked === mascot.id}
                  $destructive={false}
                  onClick={choose(mascot.id)}
                >
                  <MascotAvatar url={firstImage(mascot)} size={ART_STYLE.MASCOT_MENU_AVATAR_PX} />
                  <OptionCopy>
                    <OptionLabel raw text={nameOf(mascot)} tag="span" />
                  </OptionCopy>
                  <Check $on={picked === mascot.id} aria-hidden="true">
                    <Icon name="check" size={14} />
                  </Check>
                </Option>
              ))}
            </MenuPanel>,
            document.body,
          )
        : null}
    </>
  );
};

export default MascotPicker;
